import { useEffect, useState } from "react";

import { useShallow } from "zustand/react/shallow";

import { fetchBuilding, fetchPlace } from "@/apis/place";
import useCalendarStore from "@/store/calendarStore";
import type { PlacesByFloor } from "@/dummy/places";
import { cn } from "@/utils/cn";

interface Building {
  bldCd: string;
  bldNm: string;
  cpsCd: string;
}

interface BuildingWithPlaces {
  building: Building;
  floors: PlacesByFloor[];
}

interface PlaceTreeProps {
  onSelect?: () => void;
}

const PlaceTree = ({ onSelect }: PlaceTreeProps = {}) => {
  const [buildings, setBuildings] = useState<BuildingWithPlaces[]>([]);
  const [collapsedBuildings, setCollapsedBuildings] = useState<Record<string, boolean>>({});
  const [collapsedFloors, setCollapsedFloors] = useState<Record<string, boolean>>({});

  const [selectedPlace, setSelectedPlace] = useCalendarStore(
    useShallow(state => [state.selectedPlace, state.setSelectedPlace]),
  );

  useEffect(() => {
    const load = async () => {
      const res = await fetchBuilding();
      const buildingList: Building[] = res?.data || [];

      // "본관"을 "선교관"보다 앞에 오도록 정렬
      buildingList.sort((a, b) => {
        if (a.bldNm.includes("본관")) return -1;
        if (b.bldNm.includes("본관")) return 1;
        return a.bldNm.localeCompare(b.bldNm);
      });

      const result: BuildingWithPlaces[] = await Promise.all(
        buildingList.map(async (building) => {
          const floors = await fetchPlace(building.bldCd);
          return { building, floors };
        }),
      );

      setBuildings(result);

      // Default: 가장 첫 번째 장소 선택 (찬양사역팀실)
      if (!selectedPlace && result.length > 0) {
        const firstBuilding = result[0];
        if (firstBuilding.floors.length > 0 && firstBuilding.floors[0].places.length > 0) {
          const firstPlace = firstBuilding.floors[0].places[0];
          setSelectedPlace({ plcCd: firstPlace.plcCd, plcNm: firstPlace.plcNm });
        }
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleBuilding = (bldCd: string) => {
    setCollapsedBuildings(prev => ({ ...prev, [bldCd]: !prev[bldCd] }));
  };

  const toggleFloor = (key: string) => {
    setCollapsedFloors(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSelect = (plcCd: string, plcNm: string) => {
    setSelectedPlace({ plcCd, plcNm });
    onSelect?.();
  };

  const isSelected = (plcCd: string) => selectedPlace?.plcCd === plcCd;

  return (
    <div className="flex flex-col gap-1 overflow-y-auto py-2">
      {buildings.map(({ building, floors }) => {
        const bldCollapsed = collapsedBuildings[building.bldCd];

        return (
          <div key={building.bldCd}>
            {/* Building header */}
            <button
              type="button"
              onClick={() => toggleBuilding(building.bldCd)}
              className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-gray-50"
            >
              <span
                className={cn(
                  "text-[10px] transition-transform",
                  bldCollapsed ? "rotate-0" : "rotate-90",
                )}
              >
                ▶
              </span>
              <span className="text-xs font-bold text-gray-800">
                {building.bldNm}
              </span>
            </button>

            {!bldCollapsed && (
              <div className="pl-2">
                {floors.map(({ floor, places }) => {
                  const floorKey = `${building.bldCd}_${floor}`;
                  const floorCollapsed = collapsedFloors[floorKey];

                  return (
                    <div key={floorKey}>
                      {/* Floor header */}
                      <button
                        type="button"
                        onClick={() => toggleFloor(floorKey)}
                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-gray-50"
                      >
                        <span
                          className={cn(
                            "text-[10px] transition-transform",
                            floorCollapsed ? "rotate-0" : "rotate-90",
                          )}
                        >
                          ▶
                        </span>
                        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
                          {floor}
                        </span>
                      </button>

                      {/* Places */}
                      {!floorCollapsed && (
                        <div className="flex flex-col gap-0.5 pb-1">
                          {places.map(place => (
                            <button
                              key={place.plcCd}
                              type="button"
                              onClick={() => handleSelect(place.plcCd, place.plcNm)}
                              className={cn(
                                "rounded-md py-1.5 pl-10 pr-2 text-left text-sm transition-colors",
                                isSelected(place.plcCd)
                                  ? "bg-primary/10 font-medium text-primary"
                                  : "text-gray-600 hover:bg-blue-50/40",
                              )}
                            >
                              {place.plcNm}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Building separator */}
            {buildings.length > 1 && (
              <div className="my-1 border-t border-gray-100" />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PlaceTree;
